// The last network problem this app saw on a live call (UX spec 6.2.12), from the engine's own
// media and signaling states. It is a client-side observation: never shown as server-confirmed.
// The engine exposes current states only (no reconnect count or issue history), so transitions
// are noticed here while the call card is mounted. Call-control drops also carry what this browser's
// SIP WebSocket reported (`BridgeCall.transportEvents`): why it dropped and how many tries it took,
// each marked observed or inferred.
import type { BridgeCall, TransportEventView } from './bridge.ts';
import { failureClause } from './transportView.ts';

export type NetworkIssueKind = 'audio_reconnecting' | 'audio_lost' | 'audio_recovered' | 'controls_down' | 'controls_recovered';

/** How sure this app is: `observed` it saw directly, `inferred` it worked out from what the browser exposes. */
export type NetworkConfidence = 'observed' | 'inferred';

/** Why the call control connection dropped, from this browser's WebSocket. */
export interface NetworkCause {
  /** Engine failure kind, for example `connection_lost` or `closed_by_server`. */
  failure: string;
  closeCode: number | null;
  sipStatus: number | null;
  confidence: NetworkConfidence;
  at: number;
}

export interface NetworkIssue {
  kind: NetworkIssueKind;
  startedAt: number;
  endedAt: number | null;
  /** Control drops only, when this browser's WebSocket reported a reason. */
  cause?: NetworkCause;
  /** Control drops only: reconnect tries this browser made while the controls were down. */
  attempts?: number;
}

/** One entry of the per-call network timeline. Always this app's own observation, never server-confirmed. */
export interface NetworkTimelineEntry {
  sequence: number;
  at: number;
  kind: 'controls_reconnecting' | 'controls_failed' | 'controls_closed' | 'controls_blocked' | 'controls_registered';
  text: string;
  provenance: 'this_app';
  confidence: NetworkConfidence;
}

type Observed = Pick<BridgeCall, 'key' | 'media' | 'signaling'> & { transportEvents?: readonly TransportEventView[] | undefined };

const OBSERVED_FAILURES = new Set(['socket_not_created', 'blocked_by_csp', 'connect_timeout', 'closed_by_server', 'register_rejected', 'register_timeout']);
const TIMELINE_LIMIT = 20;

const confidenceOf = (failure: string | null): NetworkConfidence => (failure && OBSERVED_FAILURES.has(failure) ? 'observed' : 'inferred');

function causeOf(e: TransportEventView): NetworkCause {
  return { failure: e.failure ?? 'transport_error', closeCode: e.closeCode, sipStatus: e.sipStatus, confidence: confidenceOf(e.failure), at: e.at };
}

function timelineEntry(e: TransportEventView): NetworkTimelineEntry | null {
  const base = { sequence: e.sequence, at: e.at, provenance: 'this_app' as const };
  switch (e.kind) {
    case 'connect_started':
      return { ...base, kind: 'controls_reconnecting', text: `This app tried to reconnect the call controls (try ${e.attempt}).`, confidence: 'observed' };
    case 'attempt_failed': {
      const clause = failureClause({ kind: e.failure ?? 'transport_error', sipStatus: e.sipStatus });
      return { ...base, kind: 'controls_failed', text: clause ? `The call control connection failed: ${clause}.` : 'The call control connection failed.', confidence: confidenceOf(e.failure) };
    }
    case 'socket_closed':
      return e.closeCode === null ? null : { ...base, kind: 'controls_closed', text: `The call control connection closed with code ${e.closeCode}.`, confidence: 'observed' };
    case 'csp_violation':
      return { ...base, kind: 'controls_blocked', text: 'This page’s security policy blocked the call control connection.', confidence: 'observed' };
    case 'registered':
      return { ...base, kind: 'controls_registered', text: 'The call controls reconnected to the phone system.', confidence: 'observed' };
    default:
      return null;
  }
}

export class NetworkIssueTracker {
  private key: string | null = null;
  private media: Observed['media'] | null = null;
  private signaling: Observed['signaling'] | null = null;
  private audio: NetworkIssue | null = null;
  private controls: NetworkIssue | null = null;
  private latest: 'audio' | 'controls' | null = null;
  private seen = 0;
  private entries: NetworkTimelineEntry[] = [];
  /** The failure that explains the open control drop. */
  private causeEvent: TransportEventView | null = null;

  /** Feed the engine call on each render; repeated snapshots with the same states change nothing. */
  observe(call: Observed | null, now: number): NetworkIssue | null {
    if (!call) return this.last();
    const events = call.transportEvents ?? [];
    if (call.key !== this.key) {
      this.key = call.key;
      this.media = call.media;
      this.signaling = call.signaling;
      this.audio = null;
      this.controls = null;
      this.latest = null;
      this.seen = 0;
      this.entries = [];
      this.causeEvent = null;
      const fresh = this.take(events);
      // A call first seen mid-problem: the problem is real, but its start time isn't known.
      if (call.media === 'recovering' || call.media === 'lost') this.openAudio(call.media, now);
      if (call.signaling === 'degraded') this.openControls(now, fresh);
      return this.last();
    }
    const fresh = this.take(events);
    if (call.media !== this.media) {
      this.media = call.media;
      if (call.media === 'recovering' || call.media === 'lost') this.openAudio(call.media, now);
      else if (call.media === 'connected' && this.audio && this.audio.endedAt === null) this.audio = { kind: 'audio_recovered', startedAt: this.audio.startedAt, endedAt: now };
    }
    if (call.signaling !== this.signaling) {
      this.signaling = call.signaling;
      if (call.signaling === 'degraded') this.openControls(now, fresh);
      else if (this.controls && this.controls.endedAt === null) {
        this.countAttempts(fresh);
        this.controls = { ...this.controls, kind: 'controls_recovered', endedAt: now };
      }
    } else if (this.controls && this.controls.endedAt === null) {
      this.countAttempts(fresh);
    }
    return this.last();
  }

  last(): NetworkIssue | null {
    return this.latest === 'audio' ? this.audio : this.latest === 'controls' ? this.controls : null;
  }

  /** This call's call-control connection events, oldest first, each with provenance and confidence. */
  timeline(): readonly NetworkTimelineEntry[] {
    return this.entries;
  }

  /** Events not seen before, oldest first; they also extend the timeline. */
  private take(events: readonly TransportEventView[]): TransportEventView[] {
    const fresh = events.filter(e => e.sequence > this.seen).sort((a, b) => a.sequence - b.sequence);
    for (const e of fresh) {
      this.seen = Math.max(this.seen, e.sequence);
      const entry = timelineEntry(e);
      if (entry) this.entries = [...this.entries, entry].slice(-TIMELINE_LIMIT);
    }
    return fresh;
  }

  /** Counts reconnect tries and settles the drop's cause from events seen while the controls are down. */
  private countAttempts(fresh: readonly TransportEventView[]): void {
    if (!this.controls) return;
    const tries = fresh.filter(e => e.kind === 'connect_started' || e.kind === 'register_started').length;
    if (tries) this.controls = { ...this.controls, attempts: (this.controls.attempts ?? 0) + tries };
    this.causeEvent ??= fresh.find(e => e.kind === 'attempt_failed') ?? null;
    const cause = this.causeEvent;
    if (!cause) return;
    // The close details of the dropped socket can arrive after the drop was handled; they refine it.
    const close = fresh.filter(e => e.kind === 'socket_closed' && e.failure && e.attempt === cause.attempt && e.sequence > cause.sequence).at(-1);
    const next = close ? { ...causeOf(cause), failure: close.failure!, closeCode: close.closeCode, confidence: confidenceOf(close.failure) } : this.controls.cause ?? causeOf(cause);
    this.controls = { ...this.controls, cause: next };
  }

  private openAudio(media: 'recovering' | 'lost', now: number): void {
    const kind = media === 'lost' ? 'audio_lost' : 'audio_reconnecting';
    this.audio = this.audio && this.audio.endedAt === null ? { ...this.audio, kind } : { kind, startedAt: now, endedAt: null };
    this.latest = 'audio';
  }

  private openControls(now: number, fresh: readonly TransportEventView[]): void {
    if (!this.controls || this.controls.endedAt !== null) {
      // The drop is the latest failure the engine recorded just before signaling went degraded.
      const failed = fresh.filter(e => e.kind === 'attempt_failed').at(-1) ?? null;
      this.causeEvent = failed;
      this.controls = { kind: 'controls_down', startedAt: now, endedAt: null };
      this.countAttempts(failed ? fresh.filter(e => e.sequence >= failed.sequence) : fresh);
    }
    this.latest = 'controls';
  }
}

/** Provenance suffix for every live network notice. */
export const NETWORK_ISSUE_SOURCE = 'Seen by this app. The server hasn’t confirmed it.';

/** How sure the notice is. Audio states come straight from the browser's peer connection. */
export function networkIssueConfidence(issue: NetworkIssue): NetworkConfidence {
  return issue.cause?.confidence ?? 'observed';
}

/** A short reason for a control drop, or '' when this browser has none. */
function causeText(issue: NetworkIssue): string {
  const cause = issue.cause;
  if (!cause) return '';
  switch (cause.failure) {
    case 'closed_by_server':
      return cause.closeCode !== null ? ` (the phone system closed it, code ${cause.closeCode})` : ' (the phone system closed it)';
    case 'connection_lost':
      return ' (the network connection was cut off)';
    default: {
      const clause = failureClause({ kind: cause.failure, sipStatus: cause.sipStatus });
      return clause ? ` (${clause})` : '';
    }
  }
}

const triesText = (issue: NetworkIssue) => (issue.attempts && issue.attempts > 1 ? ` after ${issue.attempts} tries` : '');

export function networkIssueText(issue: NetworkIssue, clock: (epochMs: number) => string): string {
  const from = clock(issue.startedAt);
  const to = issue.endedAt === null ? '' : clock(issue.endedAt);
  switch (issue.kind) {
    case 'audio_reconnecting':
      return `This app lost the call audio at ${from} and is reconnecting.`;
    case 'audio_lost':
      return `This app lost the call audio at ${from}.`;
    case 'audio_recovered':
      return `Call audio dropped at ${from} and came back at ${to}.`;
    case 'controls_down':
      return `The call control connection dropped at ${from}${causeText(issue)}.`;
    case 'controls_recovered':
      return `The call control connection dropped at ${from}${causeText(issue)} and came back at ${to}${triesText(issue)}.`;
  }
}
